import { EveryoneSegmentId } from '@fullstory/client-api';
import { Platform } from '@fullstory/proto/fs/api/segment';
import { GetAllSegmentsResponse } from '@fullstory/proto/fs/services/search-settings';
import { SegmentDetail } from '@fullstory/proto/fs/services/dx-objects';
import { Duration, Timestamp } from '@fullstory/proto/google/protobuf';
import {
  createErrorReducer,
  createLoadingReducer,
  createRequestReducer,
} from '@fullstory/redux-util';
import { combineReducers } from '@fullstory/redux-util/utils/combineReducers';
import { isEqual } from 'lodash';
import { createAccessReducer } from '../../utils/accessControl';
import { AppBackendActions, AppBackendRequestTypes } from '../appbackend';
import { FullstoryActions, FullstoryTypes } from '../fullstory';
import { HomeRequestActions, HomeRequestTypes } from '../home';
import { MetricsActions } from '../metrics';
import { SegmentActions } from './actions';
import { InitialSegmentIds, SegmentRequestTypes, SegmentTypes } from './types';
import { SpaceActions, SpaceRequestTypes } from '../spaces';
import { InsightRequestTypes, MetricsInsightsRequestActions } from '../metricsInsights';
import { BulkActionsRequestTypes } from '../bulkActions/types';
import { BulkRequestActions } from '../bulkActions/actions';

function updateInitialStarred(
  state: InitialSegmentIds,
  segmentId: string,
  starred: boolean,
): InitialSegmentIds {
  const newState = { ...state };
  const foundIdx = newState.starred.findIndex((id: string) => id == segmentId);
  if (starred) {
    if (foundIdx == -1) {
      // We shouldn't be allowed to star an already starred segment, but just in case.
      newState.starred.push(segmentId);
    }
  } else if (foundIdx >= 0) {
    // Should have found it if we are unstarring, but just to be safe.
    newState.starred.splice(foundIdx, 1);
  }
  return newState;
}

// TODO(craigmccown): Remove multiple sources of truth in this redux state
export function initialSegmentIds(
  state: InitialSegmentIds = {
    starred: [],
    recent: [],
  },
  action: SegmentActions,
): InitialSegmentIds {
  switch (action.type) {
    case SegmentRequestTypes.GetInitialSegmentsSuccess: {
      return {
        starred: action.data.starred.map(seg => seg.segment!.segment!.id),
        recent: action.data.recent.map(seg => seg.segment!.segment!.id),
      };
    }
    case SegmentRequestTypes.SetSegmentSuccess: {
      if (action.request.body?.segment?.id) {
        // just an update, nothing needs to be updated here
        return state;
      } else {
        // a create, add to recents
        return {
          ...state,
          recent: [action.data.segment!.segment!.segment!.id, ...state.recent],
        };
      }
    }
    case SegmentRequestTypes.ArchiveSegmentSuccess: {
      const archivedSegmentId = action.request.body?.segmentId;
      return {
        recent: state.recent.filter(segmentId => segmentId !== archivedSegmentId),
        starred: state.starred.filter(segmentId => segmentId !== archivedSegmentId),
      };
    }
    case SegmentRequestTypes.StarSegmentRequest:
      if (action.body) {
        return updateInitialStarred(state, action.body.segmentId, action.body.starred);
      } else {
        return state;
      }
    case SegmentRequestTypes.StarSegmentFailure:
      if (action.request.body) {
        // assume client was going the right thing and just revert opposite of requested value
        return updateInitialStarred(
          state,
          action.request.body.segmentId,
          !action.request.body.starred,
        );
      } else {
        return state;
      }
    case SegmentRequestTypes.MarkSegmentViewedRequest: {
      const recents = (state && state.recent) || [];
      const seen = action.body?.segmentId;

      if (seen) {
        // Append to recents
        // If it was already in the list, remove it so we can move it to the top
        const foundIdx = recents.findIndex((id: string) => id == seen);
        if (foundIdx >= 0) {
          recents.splice(foundIdx, 1);
        }

        return {
          ...state,
          // don't let the recent list become longer than the max
          recent: [seen, ...recents],
        };
      }
    }
  }
  return state;
}

// Request state (loading, error, lastRequest) of GetAllSegments.
// Data from the request is not stored here (see `allSegments` and `segmentsById`), only request state.
const allSegmentsRequestReducer = createRequestReducer<null, GetAllSegmentsResponse>({
  mergeData: () => null, // Intentionally don't store data here
})(
  SegmentRequestTypes.GetAllSegmentsRequest,
  SegmentRequestTypes.GetAllSegmentsSuccess,
  SegmentRequestTypes.GetAllSegmentsFailure,
);

// all segment IDs
function allSegments(state: string[] = [], action: SegmentActions | HomeRequestActions): string[] {
  switch (action.type) {
    case SegmentRequestTypes.GetAllSegmentsSuccess:
      return action.data.segments.map(seg => seg.segment!.segment!.id);
    case HomeRequestTypes.DuplicateHomeCardSuccess:
    case SegmentRequestTypes.GetSegmentSuccess: {
      if (action.data.segment?.segment?.segment?.id) {
        return [...state, action.data.segment.segment.segment.id];
      }
      return state;
    }
    case SegmentRequestTypes.SetSegmentSuccess: {
      if (action.request.body?.segment?.id) {
        // just an update, nothing needs to be updated here
        return state;
      } else {
        // create, add to all segments
        const segments = state.slice();
        segments.push(action.data.segment!.segment!.segment!.id);
        return segments;
      }
    }
    case SegmentRequestTypes.ArchiveSegmentSuccess: {
      const archivedSegmentId = action.request.body?.segmentId;
      return state.filter(segmentId => segmentId !== archivedSegmentId);
    }
  }
  return state;
}

export type SegmentsById = { [id: string]: SegmentDetail };

// Client heavily relies on SegmentDefinition equality checks, but updates to SegmentDefinition
// can cause out-of-sync issues between server and client
// Force SegmentDefinition rsp to conform to latest client SegmentDefinition/Constraints model
const initSegment = (segmentDetail: SegmentDetail): SegmentDetail => {
  const sd = segmentDetail.segment?.segment?.segmentDefinition;
  if (sd) {
    segmentDetail.segment!.segment!.segmentDefinition = {
      userProperties: sd.userProperties ?? [],
      excludeUserProperties: sd.excludeUserProperties ?? [],
      behaviors: sd.behaviors ?? [],
      excludeBehaviors: sd.excludeBehaviors ?? [],
      constraints: {
        inOrder: sd.constraints?.inOrder ?? false,
        inSameSession: sd.constraints?.inSameSession ?? false,
        platform: sd.constraints?.platform ?? Platform.PLATFORM_INVALID,
        timeRange: sd.constraints?.timeRange ?? null,
        completeSessions: sd.constraints?.completeSessions ?? false,
        withinTime: sd.constraints?.withinTime ?? Duration.zero,
        eventThresholds: sd.constraints?.eventThresholds ?? [],
      },
      excludeConstraints: sd.excludeConstraints ?? null,
    };
  }
  return segmentDetail;
};

function segmentsById(
  state: SegmentsById = {},
  action:
    | SegmentActions
    | HomeRequestActions
    | MetricsActions
    | AppBackendActions
    | SpaceActions
    | MetricsInsightsRequestActions
    | BulkRequestActions,
): SegmentsById {
  const updateStarred = function (segmentId: string, starred: boolean): SegmentsById {
    // Handle the Request rather than the Success, so there is no delay in updating the UI
    const newState: SegmentsById = { ...state };
    const segment = newState[segmentId];
    if (segment) {
      segment.starred = starred;
    }
    return newState;
  };

  switch (action.type) {
    // We merge results from GetInitialSegments and GetAllSegments
    // On load, only initial segments are shown.
    case SegmentRequestTypes.GetAllSegmentsSuccess: {
      return mergeSegments(state, action.data.segments);
    }
    case HomeRequestTypes.DuplicateHomeCardSuccess:
    case SegmentRequestTypes.GetSegmentSuccess: {
      if (action.data.segment?.segment?.segment) {
        return {
          ...state,
          [action.data.segment.segment.segment.id]: initSegment(action.data.segment),
        };
      }
      return state;
    }
    case SegmentRequestTypes.GetInitialSegmentsSuccess: {
      let newState = mergeSegments(state, action.data.recent);
      newState = mergeSegments(newState, action.data.starred);
      if (action.data.everyone) {
        newState = mergeSegments(newState, [action.data.everyone]);
      }
      return newState;
    }
    case SegmentRequestTypes.StarSegmentRequest: {
      if (action.body) {
        return updateStarred(action.body.segmentId, action.body.starred);
      } else {
        return state;
      }
    }
    case SegmentRequestTypes.StarSegmentFailure: {
      if (action.request.body) {
        // assume client was going the right thing and just revert opposite of requested value
        return updateStarred(action.request.body.segmentId, !action.request.body.starred);
      } else {
        return state;
      }
    }
    case SegmentRequestTypes.SetSegmentSuccess: {
      // covers create, rename, and update criteria
      return {
        ...state,
        [action.data.segment!.segment!.segment!.id]: initSegment(action.data.segment!),
      };
    }
    case SegmentRequestTypes.ArchiveSegmentSuccess: {
      if (!action.request.body) {
        return state;
      }
      const { segmentId, archived } = action.request.body;
      const segment = state?.[segmentId];
      const segmentRecord = segment?.segment;
      const metadata = segmentRecord?.metadata;

      if (segment && segmentRecord && metadata) {
        return {
          ...state,
          [segmentId]: {
            ...segment,
            segment: {
              ...segmentRecord,
              metadata: {
                ...metadata,
                archived,
              },
            },
          },
        };
      } else {
        return state;
      }
    }
    case BulkActionsRequestTypes.BulkArchiveObjectsSuccess: {
      const { body } = action.request;

      if (!body) return state;

      let newState = { ...state };
      const segmentsReq = body.segments;

      if (segmentsReq != null) {
        segmentsReq.segments.forEach(m => {
          const { segmentId, archived } = m;
          const segment = newState?.[segmentId];
          const segmentRecord = segment?.segment;
          const metadata = segmentRecord?.metadata;

          if (segment && segmentRecord && metadata) {
            newState = {
              ...newState,
              [segmentId]: {
                ...segment,
                segment: {
                  ...segmentRecord,
                  metadata: { ...metadata, archived },
                },
              },
            };
          }
        });
      }

      return newState;
    }
    case SegmentRequestTypes.MarkSegmentViewedSuccess: {
      const segmentId = action?.request?.body?.segmentId;
      if (segmentId) {
        const prevSegment = state[segmentId];
        if (prevSegment) {
          return {
            ...state,
            [segmentId]: { ...prevSegment, lastViewed: Timestamp.now() },
          };
        }
      }
      return state;
    }
    case SpaceRequestTypes.GetSpaceCardViewSuccess: {
      const updated = { ...state };
      const allObjects = action.data.sections.flatMap(s => s.cards.flatMap(c => c.object));

      for (const object of allObjects) {
        if (object?.segment && object.segment?.segment?.segment?.id) {
          updated[object.segment.segment.segment.id] = object.segment;
        }
      }

      return updated;
    }
    case SpaceRequestTypes.GetLibraryViewSuccess: {
      const segments = action.data.objects.reduce<SegmentsById>((acc, o) => {
        if (o.segment?.segment?.segment?.id) acc[o.segment?.segment?.segment?.id] = o.segment;
        return acc;
      }, {});

      return Object.keys(segments).length
        ? {
            ...state,
            ...segments,
          }
        : state;
    }
    case InsightRequestTypes.GetInsightDetailViewSuccess:
    case AppBackendRequestTypes.GetDashboardDetailViewSuccess:
    case AppBackendRequestTypes.GetFunnelDetailViewSuccess: // fallthrough
    case AppBackendRequestTypes.GetHeatmapDetailViewSuccess: // fallthrough
    case AppBackendRequestTypes.GetSegmentDetailViewSuccess: // fallthrough
    case AppBackendRequestTypes.GetHomeViewSuccess: // fallthrough
    case AppBackendRequestTypes.GetMetricDetailViewSuccess: // fallthrough
    case AppBackendRequestTypes.GetRetentionMetricDetailViewSuccess: // fallthrough
    case AppBackendRequestTypes.GetSidebarSuccess: {
      if (!action.data.deps?.segments) {
        return state;
      }

      return mergeSegments(state, Object.values(action.data.deps?.segments));
    }
  }
  return state;
}

function followedSegments(
  state: string[] = [],
  action: SegmentActions | FullstoryActions,
): string[] {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.SeatSettings?.FollowedSegments ?? [];
    case SegmentRequestTypes.FollowSegmentSuccess: {
      const body = action.request.body;
      if (body) {
        if (body.followed) {
          return state.concat(body.segmentId);
        } else {
          return state.filter(segmentId => segmentId !== body.segmentId);
        }
      }
      return state;
    }
  }
  return state;
}

function lastKnownSegment(state: string = EveryoneSegmentId, action: SegmentActions): string {
  switch (action.type) {
    case SegmentTypes.SetLastKnownSegment:
      return action.segmentId;
  }
  return state;
}

type InitialSegmentIdsReducerState = {
  loading: ReturnType<ReturnType<typeof createLoadingReducer>>;
  error: ReturnType<ReturnType<typeof createErrorReducer>>;
  data: ReturnType<typeof initialSegmentIds>;
};

const initialSegmentIdsReducer = combineReducers<InitialSegmentIdsReducerState>({
  loading: createLoadingReducer(
    SegmentRequestTypes.GetInitialSegmentsRequest,
    SegmentRequestTypes.GetInitialSegmentsSuccess,
    SegmentRequestTypes.GetInitialSegmentsFailure,
  ),
  error: createErrorReducer(
    SegmentRequestTypes.GetInitialSegmentsRequest,
    SegmentRequestTypes.GetInitialSegmentsSuccess,
    SegmentRequestTypes.GetInitialSegmentsFailure,
  ),
  data: initialSegmentIds,
});

const access = createAccessReducer(
  SegmentRequestTypes.SegmentAccessRequest,
  SegmentRequestTypes.SegmentAccessSuccess,
  SegmentRequestTypes.SegmentAccessFailure,
);

type SegmentReducerState = {
  allSegments: ReturnType<typeof allSegments>;
  allSegmentsRequest: ReturnType<typeof allSegmentsRequestReducer>;
  initialSegmentIds: ReturnType<typeof initialSegmentIdsReducer>;
  segmentsById: ReturnType<typeof segmentsById>;
  followedSegments: ReturnType<typeof followedSegments>;
  lastKnownSegment: ReturnType<typeof lastKnownSegment>;
  access: ReturnType<typeof access>;
};

export const segmentReducer = combineReducers<SegmentReducerState>({
  allSegments,
  allSegmentsRequest: allSegmentsRequestReducer,
  initialSegmentIds: initialSegmentIdsReducer,
  segmentsById,
  followedSegments,
  lastKnownSegment,
  access,
});

const mergeSegments = (state: SegmentsById, segments: SegmentDetail[]): SegmentsById => {
  if (!segments) {
    return state;
  }

  const newState = { ...state };
  segments.forEach(segment => {
    if (!segment) {
      return;
    }

    const segmentId = segment?.segment?.segment?.id;
    if (!segmentId) {
      return;
    }

    if (isEqual(state[segmentId], initSegment(segment))) {
      return;
    }

    newState[segmentId] = initSegment(segment);
  });

  return newState;
};
